/**
 * 🐋 SpeciesPicker - Cetalingua FM (React Web)
 * Target path: /src/components/shared/SpeciesPicker.js
 * NEW component
 * 
 * Reusable species selection widget for web dashboard.
 * Used by CompanyProfile.js (advertiser) and AdminDashboard.js.
 * Shows all 88 species from speciesData.js grouped by category
 * with search, select-all per category, and collapsible sections.
 * 
 * Species are stored as numeric IDs (1-88).
 */

import React, { useState, useMemo } from 'react';
import { SPECIES_DATA } from '../../shared/speciesData';
import { SPECIES_CATEGORIES } from '../../shared/advertiserConstants';
import { getSpeciesArray, getSpeciesByCategory } from '../../shared/speciesData';
import './SpeciesPicker.css';

const GROUPED_SPECIES = SPECIES_CATEGORIES
  .filter(cat => cat.id !== 'unknown')
  .map(cat => ({
    ...cat,
    species: getSpeciesByCategory(cat.id),
  }));

const SpeciesPicker = ({ selectedSpecies = [], onChange, disabled = false, showScientific = true }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({
    whale: true,
    dolphin: false,
    porpoise: false,
    manatee: false,
  });

  // Filter by search
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return GROUPED_SPECIES;
    const lower = searchQuery.toLowerCase();
    return GROUPED_SPECIES.map(group => ({
      ...group,
      species: group.species.filter(s =>
        s.name.toLowerCase().includes(lower) ||
        s.scientific.toLowerCase().includes(lower)
      ),
    })).filter(group => group.species.length > 0);
  }, [searchQuery]);

  // Expand all when searching
  const effectiveExpanded = useMemo(() => {
    if (searchQuery.trim()) {
      const expanded = {};
      filteredGroups.forEach(g => { expanded[g.id] = true; });
      return expanded;
    }
    return expandedCategories;
  }, [searchQuery, filteredGroups, expandedCategories]);

  const toggleCategory = (categoryId) => {
    if (searchQuery.trim()) return;
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const toggleSpecies = (speciesId) => {
    if (disabled) return;
    const newSelection = selectedSpecies.includes(speciesId)
      ? selectedSpecies.filter(id => id !== speciesId)
      : [...selectedSpecies, speciesId];
    onChange(newSelection);
  };

  const toggleSelectAll = (categorySpecies) => {
    if (disabled) return;
    const catIds = categorySpecies.map(s => s.id);
    const allSelected = catIds.every(id => selectedSpecies.includes(id));
    let newSelection;
    if (allSelected) {
      newSelection = selectedSpecies.filter(id => !catIds.includes(id));
    } else {
      const newSet = new Set([...selectedSpecies, ...catIds]);
      newSelection = Array.from(newSet);
    }
    onChange(newSelection);
  };

  return (
    <div className="species-picker">
      {/* Search */}
      <div className="sp-search">
        <span className="sp-search-icon">🔍</span>
        <input
          type="text"
          placeholder="Search species by name or scientific name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          disabled={disabled}
          className="sp-search-input"
        />
        {searchQuery && (
          <button className="sp-search-clear" onClick={() => setSearchQuery('')}>✕</button>
        )}
      </div>

      {/* Selection summary */}
      <div className="sp-summary">
        {selectedSpecies.length} species selected
        {selectedSpecies.length > 0 && !disabled && (
          <button className="sp-clear-all" onClick={() => onChange([])}>Clear all</button>
        )}
      </div>

      {/* Category sections */}
      <div className="sp-categories">
        {filteredGroups.map(group => {
          const isExpanded = effectiveExpanded[group.id] || false;
          const selCount = group.species.filter(s => selectedSpecies.includes(s.id)).length;
          const allSelected = group.species.length > 0 && selCount === group.species.length;

          return (
            <div key={group.id} className="sp-category">
              {/* Category header */}
              <div
                className="sp-category-header"
                onClick={() => toggleCategory(group.id)}
              >
                <div className="sp-category-left">
                  <span className="sp-category-emoji">{group.emoji}</span>
                  <span className="sp-category-name">{group.name}</span>
                  <span className="sp-category-badge">
                    {selCount > 0 ? `${selCount}/` : ''}{group.species.length}
                  </span>
                </div>
                <span className="sp-category-chevron">{isExpanded ? '▲' : '▼'}</span>
              </div>

              {/* Expanded list */}
              {isExpanded && (
                <div className="sp-category-body">
                  {/* Select all */}
                  <label className="sp-select-all" onClick={(e) => { e.preventDefault(); toggleSelectAll(group.species); }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => {}}
                      disabled={disabled}
                    />
                    <span className="sp-select-all-text">
                      {allSelected ? 'Deselect' : 'Select'} all {group.name.toLowerCase()}
                    </span>
                  </label>

                  {/* Species items */}
                  {group.species.map(species => {
                    const isSelected = selectedSpecies.includes(species.id);
                    return (
                      <label
                        key={species.id}
                        className={`sp-species-row ${isSelected ? 'sp-selected' : ''}`}
                        onClick={(e) => { e.preventDefault(); toggleSpecies(species.id); }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          disabled={disabled}
                        />
                        <div className="sp-species-info">
                          <span className="sp-species-name">{species.name}</span>
                          {showScientific && (
                            <span className="sp-species-scientific">{species.scientific}</span>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {filteredGroups.length === 0 && searchQuery.trim() && (
          <div className="sp-no-results">
            No species match "{searchQuery}"
          </div>
        )}
      </div>
    </div>
  );
};

export default SpeciesPicker;
